import React from "react";
import algoliasearch from "algoliasearch";
import { getAlgoliaResults } from "@algolia/autocomplete-preset-algolia";
import { createNullCache } from "@algolia/cache-common";
import { AutocompleteContainer } from "./AutocompleteContainer";
import { buildUrl, PERMISSIONS, ROUTES } from "../../config";
import { useHistory } from "react-router";
import { BaseItem } from "@algolia/autocomplete-core";
import { AutocompletePersonItem } from "./AutocompletePersonItem";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { clientsSearchOptions, leadsSearchOptions, userInfo } from "../../config/recoil/atoms";
import { usePermissions } from "../../hooks";
import { GetSources } from "@algolia/autocomplete-js";

const appId = process.env.REACT_APP_ALGOLIA_APP_ID || "";
const apiKey = process.env.REACT_APP_ALGOLIA_API_KEY || "";
const index = process.env.REACT_APP_ALGOLIA_INDEX || "";

// Do not cache search results. So deletes and converting lead to client
// are reflected instanly.
const searchClient = algoliasearch(appId, apiKey, { responsesCache: createNullCache() });
const HITS_PER_PAGE = 8;

interface AlgoliaQuery {
    indexName: string;
    query: string;
    params: {
        filters: string;
        hitsPerPage: number;
    };
}

export function Autocomplete() {
    const history = useHistory();
    const setClientOptions = useSetRecoilState(clientsSearchOptions);
    const setLeadOptions = useSetRecoilState(leadsSearchOptions);
    const user = useRecoilValue(userInfo);
    const { checkPermission } = usePermissions();

    const getSearchQueries = (query: string): AlgoliaQuery[] => {
        const roleFields = [
            "agent_id",
            "telemarketer",
            "loan_officer",
            "funding_agent",
            "real_estate_agent",
            "customer_service_rep",
        ];
        const roleFilter = roleFields
            .map((field) => `${field}:"${user.person_id}"`)
            .join(" OR ");

        const canReadLeadAll = checkPermission(PERMISSIONS.LEAD_READ);
        const canReadLeadOwn = checkPermission(PERMISSIONS.LEAD_READ_OWN);
        const canReadClientAll = checkPermission(PERMISSIONS.CLIENT_READ);
        const canReadClientOwn = checkPermission(PERMISSIONS.CLIENT_READ_OWN);
        const canReadClientBankruptcy = checkPermission(PERMISSIONS.CLIENT_READ_BANKRUPTCY);

        const filters: string[] = [];

        if (canReadLeadAll) filters.push('type:"credit_lead"');
        else if (canReadLeadOwn) filters.push(`type:"credit_lead" AND (${roleFilter})`);

        if (canReadClientAll) filters.push('type:"credit_client"');
        else if (canReadClientOwn) filters.push(`type:"credit_client" AND (${roleFilter})`);
        else if (canReadClientBankruptcy)
            filters.push('type:"credit_client" AND bankruptcy:true');

        if (filters.length === 0) {
            return [
                {
                    indexName: index,
                    query,
                    params: {
                        filters: 'objectID:"__no_results__"',
                        hitsPerPage: HITS_PER_PAGE,
                    },
                },
            ];
        }

        return filters.map((filter) => ({
            indexName: index,
            query,
            params: {
                filters: filter,
                hitsPerPage: HITS_PER_PAGE,
            },
        }));
    };

    const sources: GetSources<PersonAlgolia> = () => [
        {
            sourceId: "person",
            getItems({ query }: any) {
                if (!query || query === "") {
                    return [];
                }

                return getAlgoliaResults({
                    searchClient,
                    queries: getSearchQueries(query),
                });
            },
            onActive({ item, setContext }: any) {
                setContext({ preview: item });
            },
            onSelect(params: any) {
                const path =
                    params.item.type === "credit_lead"
                        ? ROUTES.SINGLE_LEADS
                        : ROUTES.SINGLE_CLIENTS;
                setClientOptions(undefined);
                setLeadOptions(undefined);
                history.push(buildUrl(path, { id: params.item.objectID }));
            },
            templates: {
                item({ item, components }: any) {
                    return <AutocompletePersonItem personItem={item} components={components} />;
                },
            },
        },
    ];

    return (
        <AutocompleteContainer
            getSources={sources}
            placeholder={"Search here..."}
            openOnFocus={true}
            detachedMediaQuery={""}
        />
    );
}

export interface PersonAlgolia extends BaseItem {
    objectID: string;
    first_name?: string;
    last_name?: string;
    spouse_first_name?: string;
    spouse_last_name?: string;
    email?: string;
    phone?: string;
    address?: string;
    social_security_number?: string;
    created_at?: string;
    type?: string;
}
